import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, saveSession } from "@/lib/api";
import Navbar from "@/components/karmivo/Navbar";
import { HardHat, CheckCircle2, Upload, Loader2 } from "lucide-react";

export const WORK_CATEGORIES = [
  { key: "loading_unloading", en: "Loading & Unloading", hi: "लोडिंग एवं अनलोडिंग" },
  { key: "daily_labour", en: "Daily Labour", hi: "दैनिक मजदूरी" },
  { key: "tiles_work", en: "Tiles Work", hi: "टाइल्स का काम" },
  { key: "house_repair", en: "House Repair & Renovation", hi: "मकान मरम्मत एवं नवीनीकरण" },
];
const ID_TYPES = [["aadhaar", "Aadhaar (12 digits)"], ["pan", "PAN"], ["voter", "Voter ID"], ["driving_licence", "Driving Licence"]];
const MAX = 5 * 1024 * 1024;
const ID_RULES = { aadhaar: /^\d{12}$/, pan: /^[A-Z]{5}\d{4}[A-Z]$/, voter: /^[A-Z0-9]{8,12}$/, driving_licence: /^[A-Z0-9-]{10,20}$/ };
const ID_HINT = { aadhaar: "Aadhaar must be 12 digits", pan: "PAN like ABCDE1234F", voter: "Voter ID 8-12 letters/digits", driving_licence: "10-20 letters/digits" };
const PHONE = /^[6-9]\d{9}$/;

const EMPTY = { name: "", father_name: "", phone: "", date_of_birth: "", address: "", city: "", district: "", state: "Rajasthan",
  pincode: "", emergency_contact: "", experience_years: "", categories: [], email: "", password: "",
  id_proof_type: "aadhaar", id_proof_number: "", public_photo_consent: true };

function check(f, photo, idDoc) {
  const e = {};
  if (f.name.trim().length < 2) e.name = "Enter your full name";
  if (f.father_name.trim().length < 2) e.father_name = "Enter your father's name";
  if (!PHONE.test(f.phone)) e.phone = "10-digit mobile number";
  if (!f.date_of_birth) e.date_of_birth = "Required";
  else {
    const b = new Date(`${f.date_of_birth}T00:00:00`), t = new Date();
    const age = t.getFullYear() - b.getFullYear() - (t < new Date(t.getFullYear(), b.getMonth(), b.getDate()) ? 1 : 0);
    if (age < 18 || age > 70) e.date_of_birth = "You must be 18-70 years old";
  }
  if (f.address.trim().length < 10) e.address = "Enter your complete address";
  ["city", "district", "state"].forEach((k) => { if (f[k].trim().length < 2) e[k] = "Required"; });
  if (!/^[1-9]\d{5}$/.test(f.pincode)) e.pincode = "6-digit PIN code";
  if (!PHONE.test(f.emergency_contact)) e.emergency_contact = "10-digit number";
  else if (f.emergency_contact === f.phone) e.emergency_contact = "Must differ from your mobile";
  if (f.experience_years === "" || +f.experience_years < 0 || +f.experience_years > 50) e.experience_years = "0-50 years";
  if (!f.categories.length) e.categories = "Select at least one";
  if (f.password.length < 8) e.password = "At least 8 characters";
  const idn = f.id_proof_number.replace(/\s/g, "").toUpperCase();
  if (!idn) e.id_proof_number = "Required";
  else if (!ID_RULES[f.id_proof_type].test(idn)) e.id_proof_number = ID_HINT[f.id_proof_type];
  if (!photo) e.photo = "Upload a passport size photo";
  if (!idDoc) e.id_document = "Upload your ID proof";
  return e;
}

function Field({ label, err, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
      {err && <p className="text-xs text-red-600 mt-1">{err}</p>}
    </div>
  );
}

function FilePick({ id, label, hint, accept, file, onPick, err, preview }) {
  return (
    <Field label={label} err={err}>
      <label htmlFor={id} className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-3 cursor-pointer hover:border-orange-400 transition-colors">
        {preview ? <img src={preview} alt="" className="w-12 h-16 object-cover rounded"/> : <Upload size={18} className="text-orange-500"/>}
        <span className="text-sm text-slate-600 truncate">{file ? file.name : hint}</span>
      </label>
      <input id={id} type="file" accept={accept} className="hidden" data-testid={id}
        onChange={(e) => { const x = e.target.files?.[0]; if (!x) return; if (x.size > MAX) { toast.error("File must be 5 MB or smaller"); return; } onPick(x); }}/>
    </Field>
  );
}

export default function WorkerRegister() {
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [photo, setPhoto] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [errs, setErrs] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleCat = (k) => upd("categories", f.categories.includes(k) ? f.categories.filter((c) => c !== k) : [...f.categories, k]);
  const inp = (k, props = {}) => <Input value={f[k]} onChange={(e) => upd(k, e.target.value)} data-testid={`w-${k}`} {...props}/>;

  const submit = async () => {
    const e = check(f, photo, idDoc); setErrs(e);
    if (Object.keys(e).length) { toast.error("Please fix the highlighted fields"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("data", JSON.stringify({ ...f, experience_years: +f.experience_years, email: f.email.trim() || undefined }));
      fd.append("photo", photo); fd.append("id_document", idDoc);
      const { data } = await api.post("/workers/register", fd);
      saveSession("worker", data);
      setDone(data.message);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join(", ") : d || "Could not submit");
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-slate-50"><Navbar/>
      <div className="max-w-xl mx-auto px-6 py-16">
        <Card className="p-8 text-center" data-testid="w-register-success">
          <CheckCircle2 size={44} className="mx-auto text-emerald-500"/>
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Application submitted</h1>
          <p className="text-slate-600 mt-3 leading-relaxed" data-testid="w-register-success-msg">{done}</p>
          <Button className="mt-6 bg-orange-500 hover:bg-orange-600" onClick={() => nav("/worker/dashboard")} data-testid="w-go-dashboard">View application status</Button>
        </Card>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50"><Navbar/>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 bg-orange-500 text-white rounded-xl flex items-center justify-center"><HardHat size={20}/></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Join Karmivo as a Worker</h1>
            <p className="text-sm text-slate-500">Every application is checked by a Karmivo Admin. Your Worker ID is issued after verification.</p>
          </div>
        </div>
        <Card className="p-5 sm:p-6 space-y-6">
          <section className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *" err={errs.name}>{inp("name")}</Field>
            <Field label="Father's Name *" err={errs.father_name}>{inp("father_name")}</Field>
            <Field label="Mobile Number *" err={errs.phone}>{inp("phone", { inputMode: "numeric", maxLength: 10 })}</Field>
            <Field label="Date of Birth *" err={errs.date_of_birth}>{inp("date_of_birth", { type: "date" })}</Field>
            <Field label="Complete Residential Address *" err={errs.address} className="sm:col-span-2">
              <Textarea value={f.address} onChange={(e) => upd("address", e.target.value)} data-testid="w-address"/>
            </Field>
            <Field label="City *" err={errs.city}>{inp("city")}</Field>
            <Field label="District *" err={errs.district}>{inp("district")}</Field>
            <Field label="State *" err={errs.state}>{inp("state")}</Field>
            <Field label="PIN Code *" err={errs.pincode}>{inp("pincode", { inputMode: "numeric", maxLength: 6 })}</Field>
            <Field label="Emergency Contact Number *" err={errs.emergency_contact}>{inp("emergency_contact", { inputMode: "numeric", maxLength: 10 })}</Field>
            <Field label="Work Experience (years) *" err={errs.experience_years}>{inp("experience_years", { type: "number", min: 0, max: 50 })}</Field>
          </section>

          <Field label="Work Categories * (select all that apply)" err={errs.categories}>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="w-categories">
              {WORK_CATEGORIES.map((c) => (
                <label key={c.key} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${f.categories.includes(c.key) ? "border-orange-500 bg-orange-50" : "border-slate-200 bg-white"}`}>
                  <Checkbox checked={f.categories.includes(c.key)} onCheckedChange={() => toggleCat(c.key)} data-testid={`w-cat-${c.key}`}/>
                  <span className="text-sm"><span className="font-semibold text-slate-800">{c.en}</span><span className="block text-slate-500">{c.hi}</span></span>
                </label>
              ))}
            </div>
          </Field>

          <section className="grid sm:grid-cols-2 gap-4">
            <FilePick id="w-photo" label="Passport Size Photograph *" hint="JPG, PNG or WEBP, max 5 MB" accept="image/jpeg,image/png,image/webp"
              file={photo} onPick={setPhoto} err={errs.photo} preview={photo && URL.createObjectURL(photo)}/>
            <FilePick id="w-id-document" label="ID Proof Document *" hint="Photo or PDF, max 5 MB" accept="image/jpeg,image/png,image/webp,application/pdf"
              file={idDoc} onPick={setIdDoc} err={errs.id_document}/>
            <Field label="ID Proof Type">
              <Select value={f.id_proof_type} onValueChange={(v) => upd("id_proof_type", v)}>
                <SelectTrigger data-testid="w-id-type"><SelectValue/></SelectTrigger>
                <SelectContent>{ID_TYPES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="ID Proof Number *" err={errs.id_proof_number}>{inp("id_proof_number")}</Field>
            <Field label="Email (optional)">{inp("email", { type: "email" })}</Field>
            <Field label="Create Password *" err={errs.password}>{inp("password", { type: "password", autoComplete: "new-password" })}</Field>
          </section>

          <label className="flex items-start gap-3 text-sm text-slate-600">
            <Checkbox checked={f.public_photo_consent} onCheckedChange={(v) => upd("public_photo_consent", !!v)} data-testid="w-photo-consent"/>
            <span>Show my photo on the public Karmivo ID verification page when someone scans my ID card. Your mobile number, address and documents are never shown.</span>
          </label>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-500">Your documents are stored privately and seen only by Karmivo Admins.</p>
            <Button className="bg-orange-500 hover:bg-orange-600" onClick={submit} disabled={loading} data-testid="w-submit-btn">
              {loading && <Loader2 size={16} className="animate-spin mr-2"/>}Submit Application
            </Button>
          </div>
          <div className="text-xs text-slate-500 text-center">Already registered? <Link to="/worker/login" className="text-orange-600 font-semibold" data-testid="w-login-link">Log in</Link></div>
        </Card>
      </div>
    </div>
  );
}
